import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cn from 'classnames';

import { evExplicitSearch, evTopLevelNavigationClick } from 'events/headerFooter';
import { SESSIONSTORAGE_KEY_REWARDS_INFO } from 'constants/rewardsInfo';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { track } from 'apis/amethyst';
import Link from 'components/hf/HFLink';
import SkipLinks from 'components/common/SkipLinks';
import { CHECKOUT_URL_RE } from 'common/regex';
import { isDesktop, makeOpenLiveChat, removeFromSessionStorage } from 'helpers/ClientUtils';
import { getSearchInputValue } from 'helpers/HFHelpers';
import { onEvent } from 'helpers/EventHelpers';
import { isMobileContentBreakpoint } from 'helpers/HtmlHelpers';
import { termEncoder } from 'helpers/SearchUtils';
import { stripSpecialCharsDashReplace as strip } from 'helpers';
import { showCartModal } from 'actions/cart';
import {
  getCartCount,
  handleHFSearchChange,
  handleSearchKeyDown,
  searchByTerm,
  setCartCountCookie,
  setHFSearchSuggestionsActiveIndex,
  setHFSearchTerm
} from 'actions/headerfooter';
import LandingPageImage from 'components/landing/LandingPageImage';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import SearchSuggestions from 'components/hf/SearchSuggestions';
import Banner from 'components/hf/6pm/Banner';
import HeaderNav from 'components/hf/6pm/HeaderNav';
import marketplace from 'cfg/marketplace.json';

import css from 'styles/containers/hf/6pm/header.scss';

const { homepage, phoneNumber, phoneNumberTel, chatUrl, chatWindowName } = marketplace;

const openLiveChat = makeOpenLiveChat(chatWindowName);

const NAV_OPEN_CLASS = 'navOpen';

export class Header extends Component {
  static displayName = 'Header';
  state = {
    accountExpanded: false,
    forceHideSubNav: true,
    showMiniHeader: false
  };

  componentDidMount() {
    const { getCartCount, handleSearchKeyDown } = this.props;
    getCartCount();

    // Handle clicks on the body to close any open dropdown
    onEvent(window, 'click', this.handleDocClick, null, this);

    // Handle key events for navigating search suggestions
    onEvent(window, 'keydown', handleSearchKeyDown, null, this);

    // Ensure nav isn't being openned on load
    this.handleNavHide();

    // See if we need the small version of the header
    this.checkForSmallerHeader();
  }

  componentDidUpdate(prevProps) {
    const { cartCount: prevCartCount, location: { pathname: prevPathname } } = prevProps;
    const { cartCount, setCartCountCookie, location: { pathname } } = this.props;

    // Keep cart-count cookie in sync with state
    if (cartCount !== prevCartCount && typeof cartCount === 'number') {
      setCartCountCookie(cartCount);
    }

    if (prevPathname !== pathname) {
      // Close navs to prevent persisting overlaying during client-routing
      this.handleNavHide();
      document.body.classList.remove(NAV_OPEN_CLASS);

      this.checkForSmallerHeader();
    }
  }

  handleDocClick = e => {
    this.handleDocClickForNav(e);
    this.handleDocClickForAccountMobileDropdown(e);
  };

  handleDocClickForNav = e => {
    const inNav = e.target.closest('[data-sub-nav]');
    const isOpen = document.body.classList.contains(NAV_OPEN_CLASS);

    if (e.target.closest('[data-nav-toggle]')) {
      document.body.classList.toggle(NAV_OPEN_CLASS);
    } else if (!inNav && isOpen) {
      document.body.classList.remove(NAV_OPEN_CLASS);
    }
  };

  handleNavHide = () => {
    // Clear focus to prevent automatically reopenning nav after timeout
    if (document.activeElement?.closest('header')) {
      document.activeElement.blur();
    }
    // Temporarily hide navmenu in order to keep it from remaining open during client routing
    this.setState({ forceHideSubNav: true });
    setTimeout(() => {
      this.setState({ forceHideSubNav: false });
    }, 600);
  };

  handleDocClickForAccountMobileDropdown = e => {
    const isMobile = isMobileContentBreakpoint(document.body);
    const { accountExpanded } = this.state;
    if (isMobile && !e.target.dataset.headerAccountToggle && accountExpanded) {
      this.setState({ accountExpanded: false });
    }
  };

  handleAccountToggleClick = e => {
    trackLegacyEvent('Global', 'Header', 'My-Account');
    trackEvent('TE_HEADER_ACCOUNT_DROPDOWN');

    if (isMobileContentBreakpoint(document.body)) {
      e.preventDefault();
      const { accountExpanded } = this.state;
      this.setState({ accountExpanded: !accountExpanded });
    }
  };

  checkForSmallerHeader = () => {
    const { location: { pathname } } = this.props;
    const showMiniHeader = !!pathname.match(CHECKOUT_URL_RE);
    this.setState({ showMiniHeader });
  };

  makeSkipLink = () => {
    const links = { main: 'Skip to main content' };
    return (<SkipLinks links={links}/>);
  };

  makeLogo = () => {
    const { testId } = this.context;
    const { content } = this.props;
    const data = content?.Header?.slotData?.['header-2']?.images?.[0];
    if (data) {
      const { href, gae, alt, src, retina, height, width } = data;
      const imageProps = { src, retina, width, height, alt };
      return (
        <Link
          to={href || '/'}
          className={css.logo}
          onClick={() => {
            const evt = gae || alt;
            trackLegacyEvent('Global-Header-sixpmrefreshheader', 'Promos', strip(evt));
            trackEvent('TE_HEADER_LOGO', evt);
          }}
          data-test-id={testId('headerLogo')}>
          <LandingPageImage {...imageProps} />
        </Link>
      );
    }
    return null;
  };

  makePhoneCallout = () => {
    const { content } = this.props;
    const data = content?.Header?.slotData?.clthours;
    return data && (
      <div className={css.time}>
        {data.pageContent.body}
      </div>
    );
  };

  makeMyAccountDropdown = () => {
    const { testId } = this.context;
    const { holmes: { firstName } } = this.props;
    return (
      <div className={css.iconDropdown}>
        <Link
          data-test-id={testId('headerAccountToggle')}
          data-header-account-toggle
          aria-expanded={this.state.accountExpanded}
          to="/account"
          className={css.iconAccount}
          onClick={this.handleAccountToggleClick}>
          My Account
        </Link>
        <ul>
          {firstName ?
          <>
            <li className={css.name}>Hello, {decodeURIComponent(firstName)}</li>
            <li><Link
              data-test-id={testId('headerAccountLogout')}
              to="/logout"
              onClick={() => {
                removeFromSessionStorage(SESSIONSTORAGE_KEY_REWARDS_INFO);
                trackLegacyEvent('Global', 'Header', 'Logout');
                trackEvent('TE_HEADER_ACCOUNT_SIGNOUT');
              }}>Logout</Link></li>
          </>
            :
            <li><Link
              to="/login"
              data-test-id={testId('headerAccountLogin')}
              onClick={() => {
                trackLegacyEvent('Global', 'Header', 'Log-In-or-Register');
                trackEvent('TE_HEADER_ACCOUNT_SIGNIN');
              }}>Log In or Register</Link></li>
          }
          <li><Link
            to="/account"
            data-test-id={testId('headerAccountMyAccount')}
            onClick={() => {
              trackLegacyEvent('Global', 'Header', 'My-Account-Dropdown');
              trackEvent('TE_HEADER_ACCOUNT_MYACCOUNT');
            }}>My Account</Link></li>
        </ul>
      </div>
    );
  };

  makeBotImageBanner = () => {
    const { testId } = this.context;
    const { content } = this.props;
    const data = content?.Header?.slotData?.['header-5']?.images?.[0];
    if (data) {
      const { href, gae, alt, src, retina, width, height } = data;
      const imageProps = { src, retina, width, height, alt };
      return (
        <Link
          data-header-additional
          className={cn(css.navImageBanner, css.headerContainer)}
          to={href || '/'}
          data-test-id={testId('headerBottomBanner')}
          onClick={() => {
            trackLegacyEvent('Global-Header-sixpmrefreshheader', 'Promos', strip(gae || alt));
            trackEvent('TE_HEADER_PROMOS', `Bottom Banner ${gae || alt}`);
          }}>
          <LandingPageImage {...imageProps} />
        </Link>
      );
    }
    return null;
  };

  handleSearchSubmit = e => {
    const { props: {
      searchByTerm, suggestionIndex, suggestions, setHFSearchTerm
    }, context: { router } } = this;
    const { target } = e;
    e.preventDefault();
    const term = target['term'].value;
    const inputValueWithSuggestions = getSearchInputValue(term, suggestionIndex, suggestions);

    if (typeof suggestionIndex === 'number' && suggestions?.[suggestionIndex]) {
      // Search suggestion submit
      const item = suggestions[suggestionIndex];
      const searchObj = { term: item.suggestion };
      if (item.categories?.length) {
        // For when we're using searchsuggest with a category
        searchObj.facet = `zc2/${termEncoder(item.categories[0])}`;
      }
      searchByTerm(searchObj, router);
      trackEvent('TE_HEADER_SEARCH_SUGGESTION', inputValueWithSuggestions);
      trackLegacyEvent('Action', `Search Bar*${inputValueWithSuggestions}`, 'Type');
      setHFSearchTerm(inputValueWithSuggestions);
      track(evExplicitSearch, { term: searchObj.term });
    } else {
      // Normal header search
      searchByTerm({ term }, router);
      trackEvent('TE_HEADER_SEARCH', term);
      trackLegacyEvent('Action', `Search Bar*${term}`, 'Type');
      track(evExplicitSearch, { term });
    }
  };

  handleCartClick = e => {
    const { showCartModal, isRemote } = this.props;
    const canShowCartModal = isDesktop(marketplace) && window.location.pathname !== '/cart';

    if (canShowCartModal && !isRemote) {
      e.preventDefault();
      showCartModal(true);
    }

    trackEvent('TE_HEADER_CART');
    trackLegacyEvent('Global', 'Header', 'Cart');
    track(evTopLevelNavigationClick, {
      valueClicked: 'Header Cart'
    });
  };

  render() {
    const { testId } = this.context;
    const { content, cartCount, handleHFSearchChange, suggestions, suggestionIndex, term,
      location: { pathname } } = this.props;
    const { forceHideSubNav, showMiniHeader } = this.state;
    const hasSuggestions = !!(suggestions && suggestions.length);
    const isHome = pathname === '/' || pathname.includes(homepage);
    const {
      'header-1': topBannerData,
      'header-3': topCalloutData,
      'header-4': searchBannerData,
      navmenu
    } = content?.Header?.slotData || {};

    return (
      <>
        {this.makeSkipLink()}
        {content?.fallback && <p className={css.hidden}>***Using Fallback Header***</p>}
        <Banner
          data={topBannerData}
          type="headerTopBanner"
        />
        <header data-header-container className={cn(css.header, { [css.miniHeader]: showMiniHeader })}>
          <div className={cn(css.headerTop, css.headerContainer)}>
            {this.makeLogo()}
            {!showMiniHeader && <Banner
              data={topCalloutData}
              hideMobile={!!topBannerData}
              type="headerTopCallout"
            />}
            <div className={css.phoneHelp}>
              <div className={css.phoneCallout}>
                <div>
                  <a
                    className={css.phone}
                    data-test-id={testId('headerPhoneDesktop')}
                    href={phoneNumberTel}
                    onClick={() => {
                      trackLegacyEvent('Global', 'Header', 'Phone-Desktop');
                      trackEvent('TE_HEADER_CUSTOMER_SERVICE', 'Phone Tel Desktop');
                    }}>
                    {phoneNumber}
                  </a>
                  {this.makePhoneCallout()}
                </div>
              </div>
              <a
                className={css.liveChatCallout}
                href={chatUrl}
                data-test-id={testId('headerLiveChatDesktop')}
                onClick={e => {
                  openLiveChat(e);
                  trackLegacyEvent('Global', 'Header', 'Chat-Desktop');
                  trackEvent('TE_HEADER_CUSTOMER_SERVICE', 'Live Chat Desktop');
                }}>
                <p className={css.liveChatLink}>
                  Live Chat
                </p>
              </a>
            </div>
            <div className={css.icons}>
              {this.makeMyAccountDropdown()}
              <div className={css.hideMobile}>
                <Link
                  data-test-id={testId('headerCustomerService')}
                  to="/c/customer-service-center"
                  className={css.iconHelp}
                  onClick={() => {
                    trackLegacyEvent('Global', 'Header', 'Help');
                    trackEvent('TE_HEADER_CUSTOMER_SERVICE', 'Customer Service');
                  }}>
                  Customer service
                </Link>
              </div>
              <div>
                <Link
                  to="/account/favorites"
                  data-test-id={testId('headerFavorites')}
                  className={css.iconHeart}
                  onClick={() => {
                    trackLegacyEvent('Global', 'Header', 'Favorites');
                    trackEvent('TE_HEADER_ACCOUNT_FAVORITES');
                  }}>
                  Favorites
                </Link>
              </div>
              <div>
                <Link
                  data-test-id={testId('headerCart')}
                  to="/cart"
                  className={css.iconBag}
                  onClick={this.handleCartClick}>
                  {cartCount > 0 ?
                    <>
                      <span className={css.cartCount}>{cartCount}</span>
                      <span className={css.screenReadersOnly}>items in cart</span>
                    </>
                    : <span className={css.screenReadersOnly}>Cart empty</span>
                  }
                </Link>
              </div>
              <button
                type="button"
                className={css.mobileNavToggle}
                data-nav-toggle
                onClick={() => {
                  trackLegacyEvent('Global', 'Header', 'Mobile-Nav-Open');
                  trackEvent('TE_HEADERFOOTER_MAIN_NAV_TOGGLE');
                }}>Open Nav Menu</button>
            </div>
          </div>

          <HeaderNav
            navMenu={navmenu.navMenu}
            headerMenues={content?.HeaderMenues}
            phoneCallout={this.makePhoneCallout()}
            forceHideSubNav={forceHideSubNav}/>

          <div className={css.searchSection}>
            <div className={css.searchDropdown}>
              <div className={cn(css.headerContainer, css.searchContainer)}>
                <form
                  onSubmit={this.handleSearchSubmit}
                  id="searchForm"
                  action="/search"
                  method="GET"
                  data-test-id={testId('headerSearchForm')}
                  className={css.navSearchForm}
                  role="search">
                  <label htmlFor="searchAll" className={css.screenReadersOnly}>Search 6pm.com</label>
                  <input// eslint-disable-line jsx-a11y/no-access-key
                    id="searchAll"
                    type="search"
                    name="term"
                    role="combobox"
                    aria-controls={hasSuggestions ? 'hfSearchSuggest' : null}
                    aria-owns={hasSuggestions ? 'hfSearchSuggest' : null}
                    aria-autocomplete="list"
                    aria-haspopup="listbox"
                    aria-expanded={hasSuggestions}
                    onChange={handleHFSearchChange}
                    className={css.searchInput}
                    placeholder="Search 6pm.com"
                    accessKey="s"
                    data-test-id={testId('headerSearchInput')}
                    value={getSearchInputValue(term, suggestionIndex, suggestions)}
                    autoComplete="off"/>
                  <button
                    type="submit"
                    data-test-id={testId('headerSearchSubmit')}
                    className={css.searchSubmit}>Submit Search</button>
                  <SearchSuggestions suggestions={suggestions} suggestionIndex={suggestionIndex}/>
                </form>
                <Banner
                  data={searchBannerData}
                  hideMobile={!isHome}
                  type="searchBanner"
                />
              </div>
            </div>
          </div>
        </header>
        {isHome && this.makeBotImageBanner()}
      </>
    );
  }
}

Header.contextTypes = {
  router: PropTypes.object,// Not passed when we're a remote HF
  testId: PropTypes.func
};

function mapStateToProps(state) {
  return {
    isRemote: state.headerFooter.isRemote,
    holmes: state.holmes,
    content: state.headerFooter.content,
    term: state.headerFooter.term,
    suggestions: state.headerFooter.suggestions,
    suggestionIndex: state.headerFooter.suggestionIndex,
    cartCount: state.cart.cartCount,
    location: state.routing.locationBeforeTransitions
  };
}

const HeaderConnected = connect(mapStateToProps, {
  searchByTerm,
  getCartCount,
  handleHFSearchChange,
  setHFSearchTerm,
  setHFSearchSuggestionsActiveIndex,
  setCartCountCookie,
  showCartModal,
  handleSearchKeyDown
})(Header);

const HeaderConnectedWithErrorBoundary = withErrorBoundary('Header', HeaderConnected);
export default HeaderConnectedWithErrorBoundary;
